import { css } from '@emotion/react';
import {
  faCheck, faCirclePlus, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import React, {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonComponent, ButtonStyle, RoundButtonComponent,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { RegularDropdownComponent } from '~/_shared/baseComponents/dropdown';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { getColorsFromPalette } from '~/_shared/utils/colors/color.palette';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { ActiveBoundaryHeaderComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryHeader.component';
import { BoundaryFillSummaryTitleComponent } from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSummaryTitle.component';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import type { BoundaryTerritoryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  boundaryTerritoryGroupsRemoveRequest, boundaryTerritoryGroupsUpdateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { ModalComponent } from '../../../../../_shared/components/modal/modal.component';
import { type ModalProps } from '../../../../../modal/modalType.enum';
import { BoundaryFillSettingsContentContainer } from './boundaryFillSettingsContentContainer';

type CustomizeBoundaryFillsProps = ModalProps<{
  activeBoundaryTerritoryGroupId?: BoundaryTerritoryGroupId;
  boundaryTerritoryGroups: [null, ...NonEmptyArray<BoundaryTerritoryGroup>] | NonEmptyArray<BoundaryTerritoryGroup>;
  title: string;
  showLoading: boolean;
  onIsLoadingChange: (isLoading: boolean) => void;
}>;

const editWrapperStyle = css({
  padding: 16,
});

const dropdownTriggerStyles = css({
  flexGrow: 1,
  minWidth: 0,
});

const ddButtonComboStyles = css({
  flexWrap: 'nowrap',
  maxWidth: '100%',
});

const modalContentStyle = css({
  padding: 0,
});

const activeBoundaryHeaderStyle = css({
  height: 45,
});

const titleActionStyles = css({
  display: 'flex',
  gap: 16,
  justifyContent: 'space-between',
  alignItems: 'center',
});

const h2HeadingStyles = css({
  textTransform: 'uppercase',
  fontWeight: 400,
  fontSize: 24,
  margin: 0,
});

const fillItemWrapperStyle = (theme: Theme) => css({
  boxSizing: 'border-box',
  marginTop: 4,
  borderRadius: 4,
  borderColor: theme.borderColors.primary,
  borderWidth: 1,
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  overflow: 'hidden',
});

const introStyle = css({
  fontWeight: 400,
  fontSize: 16,
  margin: 0,
});

export const CustomizeBoundaryFillsComponent: FC<CustomizeBoundaryFillsProps> = ({
  activeBoundaryTerritoryGroupId,
  boundaryTerritoryGroups,
  isOpen,
  onClose,
  title,
  showLoading,
  onIsLoadingChange,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const activeBoundaryTerritoryGroup = boundaryTerritoryGroups.find(btg => btg?.boundaryTerritoryGroupId === activeBoundaryTerritoryGroupId);
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const { openModal: openPendingChangesModal } = useModal('ChangesPending');
  const { openModal: openAddFill } = useModal('BoundaryAddFill');
  const boundaryGroupId = activeBoundaryTerritoryGroup?.boundaryGroupId;
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryGroupId);
  const displayName = activeBoundaryTerritoryGroup ?
    getBoundaryTerritoryGroupName(activeBoundaryTerritoryGroup) :
    t('Boundary');
  const [selectedBtgId, setSelectedBtgId] = useState(activeBoundaryTerritoryGroupId);
  const [selectedBtgWorkCopy, setSelectedBtgWorkCopy] = useState(boundaryTerritoryGroups.find(btg => btg?.boundaryTerritoryGroupId === selectedBtgId));
  const [isFormValid, setIsFormValid] = useState(true);
  const selectedBtg = boundaryTerritoryGroups.find(btg => btg && (btg.boundaryTerritoryGroupId === selectedBtgId)) ?? null;
  const previousSelectedBtg = usePrevious(selectedBtg);
  const isBtgInitial = selectedBtgWorkCopy === selectedBtg;

  const deleteSelectedBTGs = useCallback(() => {
    const entity = t('Fill');

    openConfirmationModal({
      title: t('Remove {{entity}} from map', { entity }),
      confirmCaption: t('Proceed'),
      text: t('You are about to remove a {{entity}} from the map. Do you want to proceed?',
        { entity }),
      permanentConfirmSettings: {
        id: 'remove-boundary-territory-group-from-map',
        strategy: PermanentConfirmStrategy.Session,
      },
      isConfirmButtonDestructive: true,
      onCancel: () => {
        closeConfirmationModal();
      },
      onConfirm: () => {
        if (selectedBtgId) {
          dispatch(boundaryTerritoryGroupsRemoveRequest([selectedBtgId]));
        }
        else {
          console.error('No boundary territory group selected for removal');
        }
        closeConfirmationModal();
      },
    });
  },
  [closeConfirmationModal, dispatch, openConfirmationModal, selectedBtgId, t]);

  const handleBoundaryTerritoryGroupChange = useCallback((updatedBTG: BoundaryTerritoryGroup) => {
    setSelectedBtgWorkCopy(updatedBTG);
  }, []);

  const handleSave = useCallback((onDone?: () => void) => {
    if (selectedBtgWorkCopy && isFormValid) {
      dispatch(boundaryTerritoryGroupsUpdateRequest(selectedBtgWorkCopy, { onDone }));
    }
  }, [dispatch, isFormValid, selectedBtgWorkCopy]);

  // Handle save all - identify changed boundary territory groups and save them
  const handleSaveAndClose = useCallback(() => {
    if (selectedBtgWorkCopy && isFormValid) {
      dispatch(boundaryTerritoryGroupsUpdateRequest(selectedBtgWorkCopy, { onDone: onClose }));
    }
  }, [dispatch, isFormValid, onClose, selectedBtgWorkCopy]);

  const handleSelectedBtgChange = useCallback((newBtgId: BoundaryTerritoryGroupId) => {
    const changeSelectedBtg = () => {
      setSelectedBtgId(newBtgId);
      setSelectedBtgWorkCopy(boundaryTerritoryGroups.find(btg => btg?.boundaryTerritoryGroupId === newBtgId));
    };
    if (selectedBtgWorkCopy !== selectedBtg) {
      openPendingChangesModal({
        onDiscardChanges: () => {
          changeSelectedBtg();
        },
        onSaveChanges: () => {
          handleSave(changeSelectedBtg);
        },
      });
    }
    else {
      changeSelectedBtg();
    }
  }, [boundaryTerritoryGroups, handleSave, openPendingChangesModal, selectedBtg, selectedBtgWorkCopy]);

  useEffect(() => {
    if (!selectedBtg) {
      const firstBtg = boundaryTerritoryGroups.find(btg => btg);
      if (firstBtg) {
        setSelectedBtgId(firstBtg.boundaryTerritoryGroupId);

      }
    }
  }, [boundaryTerritoryGroups, selectedBtg]);

  // Sync local state when boundaryTerritoryGroups prop changes
  useEffect(() => {
    if (previousSelectedBtg !== selectedBtg) {
      setSelectedBtgWorkCopy(selectedBtg);
      setIsFormValid(true);
    }
  }, [previousSelectedBtg, selectedBtg]);

  const resetAllChanges = useCallback(() => {
    setSelectedBtgWorkCopy(boundaryTerritoryGroups.find(btg => btg?.boundaryTerritoryGroupId === selectedBtgId));
    setIsFormValid(true);
  }, [boundaryTerritoryGroups, selectedBtgId]);

  const onBtgCreated = useCallback((createdBtg: BoundaryTerritoryGroup) => {
    setSelectedBtgId(createdBtg.boundaryTerritoryGroupId);
  }, []);

  const handleFormValidityChange = useCallback((boundaryTerritoryGroupId: BoundaryTerritoryGroupId,
    isValid: boolean) => {
    if (selectedBtgWorkCopy?.boundaryTerritoryGroupId === boundaryTerritoryGroupId) {
      setIsFormValid(isValid);
    }
  }, [selectedBtgWorkCopy?.boundaryTerritoryGroupId]);

  const closeDialogHandler = useCallback(() => {
    if (isBtgInitial) {
      onClose();
    }
    else if (!isFormValid) {
      openConfirmationModal({
        title: t('Changes Pending'),
        confirmCaption: t('altfills.showInvalidFill'),
        text: (
          <>
            <p>
              {t('altfills.pendingInvalidChanges')}
            </p>
            <p>
              {t('altfills.finishConfig')}
            </p>
          </>
        ),
        onConfirm: () => {
          closeConfirmationModal();
        },
        cancelCaption: t('altfills.discardChanges'),
        onCancel: () => {
          closeConfirmationModal();
          onClose();
        },
      });
    }
    else {
      openPendingChangesModal({
        onDiscardChanges: onClose,
        onSaveChanges: handleSaveAndClose,
      });
    }
  }, [closeConfirmationModal, handleSaveAndClose, isBtgInitial, isFormValid, onClose, openConfirmationModal, openPendingChangesModal, t]);

  return (
    <ModalComponent
      onClose={closeDialogHandler}
      isOpen={isOpen}
      maxWidth={650}
      caption={title}
      contentStyle={modalContentStyle}
      additionalContent={showLoading ? <OverlayLoaderComponent /> : null}
      preventCloseOnOverlayClick
      confirmButton={(
        <Inline gap={8}>
          <TooltipComponent
            tooltipContent={isBtgInitial ? t('altfills.noChangesToSave') : !isFormValid ? t('altfills.invalidForm') : undefined}
          >
            <ButtonComponent
              buttonStyle={ButtonStyle.Secondary}
              text={t('Save')}
              onClick={() => handleSave()}
              isDisabled={!selectedBtgWorkCopy || isBtgInitial || !isFormValid}
              prefixIcon={faCheck}
            />
          </TooltipComponent>
          <TooltipComponent
            tooltipContent={isBtgInitial ? t('altfills.noChangesToSave') : !isFormValid ? t('altfills.invalidForm') : undefined}
          >
            <ButtonComponent
              text={t('altfills.saveAndClose')}
              onClick={handleSaveAndClose}
              isDisabled={!selectedBtgWorkCopy || isBtgInitial || !isFormValid}
              prefixIcon={faCheck}
            />
          </TooltipComponent>
        </Inline>
      )}
      leftFooterContent={(
        <TooltipComponent
          tooltipContent={isBtgInitial ? t('altfills.noChangesToReset') : undefined}
        >
          <ButtonComponent
            text={t('Reset Changes')}
            onClick={resetAllChanges}
            isDisabled={isBtgInitial}
            buttonStyle={ButtonStyle.Secondary}
          />
        </TooltipComponent>
      )}
    >
      <ActiveBoundaryHeaderComponent
        css={activeBoundaryHeaderStyle}
        boundaryPermissions={boundaryPermissions}
        name={displayName}
        isAiGenerated={false}
        boundaryIsOptimizedTerritoriesTrial={false}
        maxNumberOfLines={1}
      />
      <Stack gap={8} css={editWrapperStyle}>
        <Stack gap={16}>
          <div css={titleActionStyles}>
            <h2 css={h2HeadingStyles}>
              {t('overlappingUi.altFills')}
            </h2>
            <ButtonComponent
              prefixIcon={faCirclePlus}
              text={t('altfills.add')}
              onClick={() => {
                if (boundaryGroupId) {
                  openAddFill({ boundaryGroupId, onBtgCreated });
                }
              }}
            />
          </div>
          <div css={introStyle}>
            {t('altFills.editIntro')}:
          </div>
        </Stack>

        <Inline gap={8} css={ddButtonComboStyles}>
          <RegularDropdownComponent
            css={dropdownTriggerStyles}
            value={selectedBtgId}
            onChange={handleSelectedBtgChange}
            options={boundaryTerritoryGroups.map((boundaryTerritoryGroup, index) => {
              const colorPair = getColorsFromPalette(index);

              if (!boundaryTerritoryGroup) {
                return null;
              }

              return {
                value: boundaryTerritoryGroup.boundaryTerritoryGroupId,
                name: ' ',
                prefix: (
                  <BoundaryFillSummaryTitleComponent
                    key={boundaryTerritoryGroup.boundaryTerritoryGroupId}
                    data-customize-fill-btg-id={boundaryTerritoryGroup.boundaryTerritoryGroupId}
                    boundaryTerritoryGroup={boundaryTerritoryGroup}
                    orderNumber={index}
                    colors={colorPair}
                  />
                ),
              };
            }).filter(notNullsy)}
          />
          <RoundButtonComponent
            icon={faTrash}
            isDisabled={boundaryTerritoryGroups.length === 1}
            onClick={deleteSelectedBTGs}
            buttonStyle={RoundButtonStyle.DangerInverted}
            tooltipLabel={t('altfills.remove')}
          />
        </Inline>

        <div css={fillItemWrapperStyle}>
          {selectedBtgWorkCopy && (
            <BoundaryFillSettingsContentContainer
              boundaryTerritoryGroup={selectedBtgWorkCopy}
              onBoundaryTerritoryGroupChange={handleBoundaryTerritoryGroupChange}
              valueIsInitial={isBtgInitial}
              onFormValidityChange={handleFormValidityChange}
              isValid={isFormValid}
              onIsLoadingChange={onIsLoadingChange}
            />
          )}
        </div>
      </Stack>
    </ModalComponent>
  );
};
